import React, { Dispatch, useState } from 'react';
import { Col, Table, Row, Button, Modal, Space } from 'antd';
import { CloseOutlined, WarningOutlined } from '@ant-design/icons';
import { ModalContainer } from './styles';
import { ColumnType } from 'antd/lib/table';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import api from '../../../../service/api';
import { Action } from 'typesafe-actions';
import mask from '../../../../utils/mask';
import Swal from 'sweetalert2';
import { SKW_PREFIX } from '../../../../utils/constants';
import { saveAs } from 'file-saver';
import TitlePage from 'components/TitlePage';
import TitleTable from 'components/TitleTable';
import TextTable from 'components/TextTable';
import { cleanHorariosDisponiveis } from 'store/ducks/horariosDisponiveis/actions';
import { cleanOrdemCarga } from 'store/ducks/ordemCarga/actions';
import { IOrdemCarga, Pedidos } from 'store/ducks/ordemCarga/types';

const ModalConfirmaAgendamento: React.FC<{
  ordemCarga?: IOrdemCarga;
  setIsModalOrderVisible: React.Dispatch<React.SetStateAction<boolean>>;
  horarioSelecionado: string | undefined;
  atualizaTabelaEntrega?: () => void;
  atualizaTabelaColeta?: () => void;
  atualizaTabelaOrdemCarga?: () => void;
}> = ({
  setIsModalOrderVisible,
  horarioSelecionado,
  atualizaTabelaColeta,
  atualizaTabelaEntrega,
  atualizaTabelaOrdemCarga,
  ordemCarga,
}) => {
    const dispatch = useDispatch<Dispatch<Action>>();

    const [loading, setLoading] = useState<boolean>(false);

    const { token } = useSelector((state: ApplicationState) => state.auth);
    const [isModalResumoOrdemDeColeta, setIsModalResumoOrdemDeColeta] = useState(false);

    function imprimeAgendamento(codAgendamento: number) {
      Modal.confirm({
        title: 'Atenção',
        icon: <WarningOutlined />,
        content: `Deseja imprimir o agendamento?.`,
        okText: 'Sim, quero imprimir',
        cancelText: 'Não',
        centered: true,
        onOk() {
          api
            .get(
              `/call?serviceName=${SKW_PREFIX}imprimeAgendamento&token=${token}&agendamento=${codAgendamento}`,
              {
                responseType: 'arraybuffer',
              }
            )
            .then((res) => {
              const blob = new Blob([res.data], { type: 'application/pdf' });
              saveAs(blob, `AGENDAMENTO_${codAgendamento}.pdf`);
            });

          Swal.fire({
            title: `Agendamento ${codAgendamento} realizado com sucesso!`,
            html: '',
            icon: 'success',
            showConfirmButton: true,
          });
        },
        onCancel() {
          Swal.fire({
            title: `Agendamento ${codAgendamento} realizado com sucesso!`,
            html: '',
            icon: 'success',
            showConfirmButton: true,
          });
        },
      });
    }

    const handleConfirm = async () => {
      try {
        setLoading(true)
        new Promise(async (resolve, reject) => {
          try {
            const url = `call?serviceName=${SKW_PREFIX}criaAgendamento&pagina&token=${token}`;

            const agendamento = {
              codCdd: Number(ordemCarga?.codCdd),
              codEmp: Number(ordemCarga?.codEmp),
              codCliente: Number(ordemCarga?.codDestinatario),
              codMotorista: Number(ordemCarga?.codMotorista),
              ordemCarga: Number(ordemCarga?.ordemCarga),
              codTipoCarroceria: Number(ordemCarga?.codTipVeiculo),
              codTransportadora: Number(ordemCarga?.codTransportadora),
              codVeiculo: Number(ordemCarga?.codVeiculo),
              dhAgendamento: horarioSelecionado as string,
              assoalho: ordemCarga?.assoalho as string,
            };

            const response = await api.post(url, { agendamento });
            resolve(response.data);
          } catch (error) {
            reject(error);
          }
        }).then((res: any) => {
          imprimeAgendamento(Number(res.agendamento.codAgendamento));
          dispatch(cleanOrdemCarga());
          dispatch(cleanHorariosDisponiveis());
          atualizaTabelaEntrega && atualizaTabelaEntrega();
          atualizaTabelaColeta && atualizaTabelaColeta();
          atualizaTabelaOrdemCarga && atualizaTabelaOrdemCarga();
          setIsModalResumoOrdemDeColeta(false);
          setIsModalOrderVisible(false);
        });
      } catch (error: any) {
        if (error.response.data.Error) {
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error.response.data.Error,
          });
          return;
        }
        Swal.fire({
          title: 'Erro ao realizar agendamento',
          html: error?.message,
          icon: 'warning',
          showConfirmButton: true,
          timer: 3000,
        });
      } finally {
        setLoading(false)
      }
    };

    const columns: ColumnType<Pedidos>[] = [
      {
        title: <TitleTable>Nº da NF</TitleTable>,
        dataIndex: 'numNota',
        key: 'numNota',
        render: (text) => <TextTable>{text}</TextTable>,
      },
      {
        title: <TitleTable>Fornecedor</TitleTable>,
        dataIndex: 'fornecedor',
        key: 'fornecedor',
        render: (text) => {
          return text ? <TextTable>{mask('camelCase', text)}</TextTable> : 'Não informado';
        },
      },
      {
        title: <TitleTable>Peso Bruto</TitleTable>,
        dataIndex: 'pesoBruto',
        key: 'pesoBruto',

        render: (text) => {
          return <>{text ? <TextTable>{mask('peso', text)}</TextTable> : 'Não informado'}</>;
        },
      },
      {
        title: <TitleTable>Valor</TitleTable>,
        dataIndex: 'valor',
        key: 'valor',

        render: (text) => {
          return text ? <TextTable>{mask('currency', text)}</TextTable> : 'Não informado';
        },
      },

      {
        title: <TitleTable>Observação</TitleTable>,
        dataIndex: 'observacao',
        key: 'observacao',
        render: (text) => {
          return text ? (
            <TextTable>
              <div
                style={{
                  maxWidth: '300px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {text}
              </div>
            </TextTable>
          ) : (
            'Sem observação'
          );
        },
      },
    ];

    return (
      <>
        <Modal
          centered
          title={
            <Row style={{ fontSize: '18px', fontWeight: 500, padding: '10px 0' }}>
              <Col md={24} sm={24} xs={24}>
                <TitlePage>Resumo do agendamento</TitlePage>
              </Col>
            </Row>
          }
          visible={isModalResumoOrdemDeColeta}
          onCancel={() => setIsModalResumoOrdemDeColeta(false)}
          maskClosable={false}
          closable={true}
          destroyOnClose={true}
          closeIcon={<CloseOutlined />}
          keyboard={true}
          width={1050}
          footer={[
            <Space style={{ marginBottom: '10px' }}>
              <Button
                key="back"
                type="ghost"
                size="middle"
                onClick={() => setIsModalResumoOrdemDeColeta(false)}
                data-cy="btnVoltarAgendamento"
              >
                Voltar
              </Button>
              <Button
                type="primary"
                onClick={(e) => handleConfirm()}
                data-cy="btnConfirmarAgendamento"
                loading={loading}
              >
                Confirmar
              </Button>
            </Space>,
          ]}
        >
          <ModalContainer>
            <Row gutter={24}>
              <Col md={8} sm={12} xs={12}>
                <h2>Dados do motorista/ veículo</h2>

                <p>
                  Nome do motorista:
                  <strong
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      textIndent: '5px',
                    }}
                  >
                    {ordemCarga?.motorista
                      ? mask('camelCase', ordemCarga?.motorista)
                      : 'Não informado'}
                  </strong>
                </p>
                <p>
                  CPF do motorista:
                  <strong
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      textIndent: '5px',
                    }}
                  >
                    {ordemCarga?.cpfMotorista
                      ? mask('cpfCnpj', ordemCarga?.cpfMotorista)
                      : 'Não informado'}
                  </strong>
                </p>
                <p>
                  Tipo de veículo:
                  <strong
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      textIndent: '5px',
                    }}
                  >
                    {ordemCarga?.tipVei ? mask('camelCase', ordemCarga?.tipVei) : 'Não informado'}
                  </strong>
                </p>
                <p>
                  Placa de veículo:
                  <strong
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      textIndent: '5px',
                    }}
                  >
                    {ordemCarga?.placa ? ordemCarga?.placa?.toUpperCase() : 'Não informado'}
                  </strong>
                </p>
              </Col>

              <Col md={8} sm={12} xs={12}>
                <h2>Dados do agendamento</h2>
                <p>
                  CD:
                  <strong
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      textIndent: '5px',
                    }}
                  >
                    {ordemCarga?.nomeCdd ? mask('camelCase', ordemCarga?.nomeCdd) : 'Não informado'}
                  </strong>
                </p>
                <p>
                  Data:
                  <strong
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      textIndent: '5px',
                    }}
                  >
                    {horarioSelecionado ? horarioSelecionado?.split(' ')[0] : 'Não informado'}
                  </strong>
                </p>

                <p>
                  Horário:
                  <strong
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      textIndent: '5px',
                    }}
                  >
                    {horarioSelecionado ? horarioSelecionado?.split(' ')[1] + ' hs' : 'Não informado'}
                  </strong>
                </p>
                <p>
                  Transportadora:
                  <strong
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      textIndent: '5px',
                    }}
                  >
                    {ordemCarga?.clienteRetira
                      ? 'Cliente retira/entrega'
                      : ordemCarga?.transportadora
                        ? mask('transportadoraUf', ordemCarga?.transportadora)
                        : 'Não informado'}
                  </strong>
                </p>
              </Col>

              <Col md={8} sm={12} xs={12}>
                <h2>Total da nota</h2>
                <p>
                  Valor total:
                  <strong
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      textIndent: '5px',
                    }}
                  >
                    {ordemCarga?.vlrTotal ? mask('currency', ordemCarga?.vlrTotal) : 'Não informado'}
                  </strong>
                </p>
                <p>
                  Peso total:
                  <strong
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      textIndent: '5px',
                    }}
                  >
                    {ordemCarga?.pesoTotal
                      ? mask('pesoTonelada', ordemCarga?.pesoTotal)
                      : 'Não informado'}
                  </strong>
                </p>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col md={24} sm={24} xs={24}>
                <h2 style={{ margin: '20px 0' }}>Notas</h2>
                <Table
                  columns={columns}
                  dataSource={ordemCarga?.pedidos}
                  pagination={false}
                  scroll={{ y: 240 }}
                />
              </Col>
            </Row>
          </ModalContainer>
        </Modal>

        <Button
          data-cy="btnAgendarHorario"
          type="primary"
          disabled={horarioSelecionado ? false : true}
          onClick={() => setIsModalResumoOrdemDeColeta(true)}
          loading={loading}
        >
          Confirmar Agendamento
        </Button>
      </>
    );
  };

export default ModalConfirmaAgendamento;
